import * as React from 'react';
import { useIntl } from 'react-intl';
import { CmdInput, CmdModal, CmdModalFooterButton } from '@commander-services/gui-components';
import { Controller, FieldError, useForm } from 'react-hook-form';
import { useQueries } from '@tanstack/react-query';
import FormikValidatorService, {
  IValidationApiResponse,
} from '../../Services/FormikValidatorService';
import {
  getData,
  getLabels,
  getRules,
  getValidation,
  getValidationAll,
  isCheckboxCondition,
  isRangeCondition,
} from './AlarmRuleService';
import { ICheckboxGroupOption } from '../Forms/Fields/CheckboxGroup';
import { IChipInputItem } from '../Forms/Fields/ChipInput';
import { IGroupSelectOptions, IOption } from '../Forms/CmdField';
import {
  ILabelInputs,
  IRuleDataToSent,
  IRuleFormValues,
  ISelectedEditChips,
  RuleFormValue,
  TRuleForm,
} from './types';
import {
  doorsKeys,
  radioBtnTooltipsTranslationKeys,
  rangeName,
  RULE_TYPE_BINARY,
  RULE_TYPE_BINARY_GROUP,
  RULE_TYPE_RANGE,
  RULE_TYPE_WAYPOINTS,
  waypointsKeys,
} from './constants';
import { IGroupChipSelectOption, IGroupChipSelectOptions } from '../Forms/Fields/interfaces';
import { IRadioInputHookFormValue, RadioInputHookForm } from '../Forms/Fields/RadioInputHookForm';
import GroupChipSelectHookForm from '../Forms/Fields/GroupChipSelectHookForm';
import GroupSelectHookForm from '../Forms/Fields/GroupSelectHookForm';
import { IRadioInputValue } from '../Forms/Fields/RadioInput';
import CheckboxGroupHookForm from '../Forms/Fields/CheckboxGroupHookForm';
import CheckboxInputHookForm from '../Forms/Fields/CheckboxInputHookForm';
import { transformErrors, validateFields } from '../../Services/HookFormService';
import useAnalytics from '../../hooks/useAnalytics';

interface IRuleFormProps {
  rideOwnerShipValue?: string;
  ridePublicityValue?: string;
  selectedChips: ISelectedEditChips[];
  closeCallback: () => void;
  responseCallback: (
    values: IChipInputItem,
    valuesToSent: { type: keyof IRuleFormValues; value: RuleFormValue }
  ) => void;
  dataForEdit: IRuleDataToSent | null;
}

function EditRuleForm(props: IRuleFormProps): JSX.Element {
  const { formatMessage: f } = useIntl();
  const [isLoaderActive, setIsLoaderActive] = React.useState<boolean>(false);
  const [dataOptions, setDataOptions] = React.useState<
    IGroupChipSelectOptions | IRadioInputHookFormValue[] | ICheckboxGroupOption[] | null
  >(null);
  const [conditionSelectOption, setConditionSelectOption] = React.useState<IGroupSelectOptions>({});
  const [validation, setValidation] = React.useState<IValidationApiResponse | null>(null);
  const [labelInputs, setLabelInputs] = React.useState<ILabelInputs>({
    indexIdMin: 'alarms.modal.rule.min.text.default',
    indexIdMax: 'alarms.modal.rule.max.text.default',
    customLabel: 'alarms.modal.rule.custom.default',
    placeholder: 'alarms.modal.rule.placeholder.default',
    unit: '',
    type: 'range',
  });
  const [specificValidation, setSpecificValidation] = React.useState<IValidationApiResponse>();
  const { trackEvent } = useAnalytics();

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    trigger,
    watch,
    formState: { errors },
  } = useForm<IRuleFormValues>({
    mode: 'onChange',
    defaultValues: {
      condition: '',
      waypoint_entered: undefined,
      waypoint_left: undefined,
      waypoint_in: undefined,
      waypoint_out: undefined,
      bin_2: '1',
      bin_3: '1',
      analog_1: '1',
      analog_2: '1',
      ignition: '1',
      doors: [],
      movement: '1',
      ride_privacy: 'private',
      engine_rpm: '',
      vehicle_speed: '',
      engine_temperature: '',
      fuel_level_liters: '',
      fuel_level_perc: '',
      battery_voltage: '',
      throttle_position: '',
      tachometer: '',
      satellites_count: '',
      altitude: '',
      eco_drive: '',
      temp1: undefined,
      temp2: undefined,
      min: '',
      max: '',
      ev_charging_start: '1',
      ev_plugged_in: '1',
      ev_battery_level: '',
      ev_range: '',
      esp_off: '1',
      driver_seatbelt: '1',
      jamming: '0',
      factory_emulated_alarm: '1',
      car_remote_lock: '1',
      auto_geofence: '1',
      unplug: '0',
      coolant_level_low: '0',
      oil_level_low: '0',
      check_engine: '0',
    },
    resolver: async (formData) => {
      if (!validation) {
        return { values: formData, errors: {} };
      }
      const name = formData.condition;
      let rangeErros = {};

      if (specificValidation) {
        specificValidation.condition = [
          {
            param: 'required',
            value: true,
            message: f({ id: 'alarms.modal.rule.validation.required' }),
          },
        ];
        if (rangeName.includes(name)) {
          const maxValue = validation[name][1].value;

          if (formData.min.length > 0 && formData.max.length > 0) {
            specificValidation.min = [
              validation[name][0],
              validation[name][1],
              {
                param: 'required',
                value: true,
                message: f({ id: 'alarms.modal.rule.validation.min.one.input' }),
              },
            ];
            specificValidation.max = [
              validation[name][0],
              validation[name][1],
              {
                param: 'required',
                value: true,
                message: f({ id: 'alarms.modal.rule.validation.min.one.input' }),
              },
            ];
            if (Number(formData.min) >= Number(formData.max)) {
              specificValidation.min = [
                validation[name][0],
                {
                  param: 'max',
                  value: Number(formData.max),
                  message: f({ id: 'alarms.modal.rule.validation.max.values' }),
                },
                validation[name][2],
              ];
            } else {
              specificValidation.min = [
                validation[name][0],
                {
                  param: 'max',
                  value: maxValue,
                  message: f({ id: 'alarms.modal.rule.validation.max.values' }),
                },
                validation[name][2],
              ];
            }
          } else if (formData.min.length > 0) {
            specificValidation.min = [
              validation[name][0],
              validation[name][1],
              validation[name][2],
            ];
            specificValidation.max = [validation[name][0], validation[name][1]];
          } else if (formData.max.length > 0) {
            specificValidation.max = [
              validation[name][0],
              validation[name][1],
              validation[name][2],
            ];
            specificValidation.min = [validation[name][0], validation[name][1]];
          } else {
            specificValidation.min = [
              validation[name][0],
              validation[name][1],
              {
                param: 'required',
                value: true,
                message: f({ id: 'alarms.modal.rule.validation.min.one.input' }),
              },
            ];
            specificValidation.max = [
              validation[name][0],
              validation[name][1],
              {
                param: 'required',
                value: true,
                message: f({ id: 'alarms.modal.rule.validation.min.one.input' }),
              },
            ];
          }

          specificValidation[name] = [];
        }

        rangeErros = FormikValidatorService.validateByValidationApiResponse(
          formData,
          specificValidation
        );
      }

      const formValidation = {
        ...validation,
        ...specificValidation,
        condition: [
          {
            param: 'required',
            value: true,
            message: f({ id: 'form.required' }),
          },
        ],
      };
      const validateErros = validateFields(formData, formValidation);

      let formErrors: Record<string, FieldError> = transformErrors(validateErros);

      if (formData.condition) {
        formErrors = Object.keys(formErrors)
          .filter((key) => key === formData.condition)
          .reduce(
            (obj, key) => {
              obj[key] = formErrors[key];
              return obj;
            },
            {} as Record<string, FieldError>
          );
      }

      if (isRangeCondition(formData.condition)) {
        formErrors = transformErrors(rangeErros);
      }

      return {
        values: formErrors && Object.keys(formErrors).length ? {} : formData, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const convertDataForAlarmForm = (
    labelInputsType: ILabelInputs,
    dataOptionsForLabel:
      | IGroupChipSelectOptions
      | IRadioInputValue[]
      | ICheckboxGroupOption[]
      | null,
    valuesForLabels: IRuleFormValues
  ): {
    valuesForChipInput: { id: keyof IRuleFormValues; label: string };
    valuesForSent: { type: keyof IRuleFormValues; value: RuleFormValue };
  } => {
    const typeName = valuesForLabels.condition as keyof IRuleFormValues;

    let valuesForChipInput: { id: keyof IRuleFormValues; label: string } = {
      id: typeName,
      label: f({ id: labelInputsType.indexIdMin }),
    };

    let valuesForSent: { type: keyof IRuleFormValues; value: RuleFormValue } = {
      type: typeName,
      value: valuesForLabels[typeName] ?? '',
    };

    if (
      labelInputsType.type === RULE_TYPE_WAYPOINTS &&
      dataOptionsForLabel &&
      typeof valuesForLabels[typeName] === 'object' &&
      valuesForLabels[typeName] !== null
    ) {
      const waypointValues = valuesForLabels[typeName] as Record<string, string[]>;

      const selectedChips = Object.keys(waypointValues ?? {})[0];

      if (
        selectedChips &&
        Array.isArray(waypointValues[selectedChips]) &&
        waypointValues[selectedChips].length > 3
      ) {
        valuesForChipInput = {
          id: typeName,
          label: `${f({ id: labelInputsType.indexIdMin })}: ${f({
            id: 'alarm.modal.rule.waypoints.name',
          })} ${waypointValues[selectedChips].length}`,
        };
      } else {
        const firstFiltered =
          (dataOptionsForLabel as IGroupChipSelectOptions)?.[
            Object.keys(waypointValues ?? {})[0]
          ]?.filter((item) => waypointValues[selectedChips]?.includes(String(item.value))) ?? [];

        const secondFiltered =
          (dataOptionsForLabel as IGroupChipSelectOptions)?.[
            Object.keys(waypointValues ?? {})[1]
          ]?.filter(
            (item) =>
              waypointValues[selectedChips]?.includes(String(item.value)) &&
              !firstFiltered.find((firstItem) => String(firstItem.value) === String(item.value))
          ) ?? [];

        let formattedWaypoints = '';

        if (firstFiltered.length > 0) {
          firstFiltered.forEach((waypoint, index) => {
            formattedWaypoints += index === 0 ? ` ${waypoint.item}` : `, ${waypoint.item}`;
          });
          secondFiltered.forEach((waypoint) => {
            formattedWaypoints += `, ${waypoint.item}`;
          });
        } else {
          secondFiltered.forEach((waypoint, index) => {
            formattedWaypoints += index === 0 ? ` ${waypoint.item}` : `, ${waypoint.item}`;
          });
        }

        valuesForChipInput = {
          id: typeName,
          label: `${f({ id: labelInputsType.indexIdMin })}: ${formattedWaypoints}`,
        };
      }

      valuesForSent = {
        type: typeName,
        value: [...(waypointValues[selectedChips] ?? [])],
      };
    } else if (
      labelInputsType.type === RULE_TYPE_BINARY &&
      dataOptionsForLabel &&
      valuesForLabels[typeName] !== undefined &&
      valuesForLabels[typeName] !== null
    ) {
      const selectedOption = (dataOptionsForLabel as IRadioInputValue[]).find(
        (item) => String(item.value) === String(valuesForLabels[typeName])
      );

      valuesForChipInput = {
        id: typeName,
        label: selectedOption
          ? `${f({ id: labelInputsType.indexIdMin })}: ${selectedOption.item.toLowerCase()}`
          : `${f({ id: labelInputsType.indexIdMin })}: ${String(valuesForLabels[typeName])}`,
      };

      valuesForSent = {
        type: typeName,
        value: valuesForLabels[typeName],
      };
    } else if (labelInputsType.type === RULE_TYPE_RANGE) {
      const min = valuesForLabels.min ?? '';
      const max = valuesForLabels.max ?? '';

      if (min && max) {
        valuesForChipInput = {
          id: typeName,
          label: `${f({ id: labelInputsType.customLabel })}: ${min} - ${max} ${labelInputsType.unit}`,
        };
      } else if (min) {
        valuesForChipInput = {
          id: typeName,
          label: `${f({ id: labelInputsType.customLabel })}: ${f({
            id: 'alarms.modal.rule.label.from',
          })} ${min} ${labelInputsType.unit}`,
        };
      } else if (max) {
        valuesForChipInput = {
          id: typeName,
          label: `${f({ id: labelInputsType.customLabel })}: ${f({
            id: 'alarms.modal.rule.label.to',
          })} ${max} ${labelInputsType.unit}`,
        };
      } else {
        valuesForChipInput = {
          id: typeName,
          label: f({ id: labelInputsType.customLabel }),
        };
      }

      valuesForSent = {
        type: typeName,
        value: { min, max },
      };
    } else if (
      labelInputsType.type === RULE_TYPE_BINARY_GROUP &&
      dataOptionsForLabel &&
      Array.isArray(valuesForLabels[typeName]) &&
      valuesForLabels[typeName].length > 0
    ) {
      const binaryValues = valuesForLabels[typeName] as string[];

      const selectedItems = (dataOptionsForLabel as ICheckboxGroupOption[])
        .filter((item) => binaryValues.includes(String(item.value)))
        .map((item) => item.item)
        .join(', ');

      valuesForChipInput = {
        id: typeName,
        label: selectedItems
          ? `${f({ id: labelInputsType.indexIdMin })}: ${selectedItems}`
          : f({ id: labelInputsType.indexIdMin }),
      };

      valuesForSent = {
        type: typeName,
        value: binaryValues,
      };
    } else {
      valuesForChipInput = {
        id: typeName,
        label: f({ id: labelInputsType.indexIdMin }),
      };
    }

    return { valuesForChipInput, valuesForSent };
  };

  const handleOnSubmit = (values: IRuleFormValues) => {
    props.responseCallback(
      convertDataForAlarmForm(labelInputs, dataOptions, values).valuesForChipInput,
      convertDataForAlarmForm(labelInputs, dataOptions, values).valuesForSent
    );

    props.closeCallback();
  };

  const buttons: CmdModalFooterButton[] = [
    {
      id: 'close-modal',
      type: 'button',
      className: 'e-button',
      title: f({ id: 'alarms.modal.rule.button.close' }),
      tooltip: f({ id: 'alarms.modal.rule.button.close.tooltip' }),
      closeCallback: props.closeCallback,
    },
    {
      id: 'edit-rule-form-submit',
      type: 'submit',
      className: 'e-button e-button--gray',
      title: f({ id: 'alarms.modal.rule.button.addrule' }),
      tooltip: f({ id: 'alarms.modal.rule.button.addrule.tooltip' }),
      submitCallback: handleSubmit(handleOnSubmit),
    },
  ];

  const getDataForOptions = async (value: string) => {
    setIsLoaderActive(true);
    const data = await getData(value);
    setIsLoaderActive(false);

    if (data) {
      let tempOptions: Record<string, IGroupChipSelectOption[]> = {};
      const tempEditValues: string[] = [];

      if (Array.isArray(data)) {
        tempOptions = data;
      } else if (Object.values(data)[1].length > 0 && Array.isArray(props.dataForEdit?.value)) {
        props.dataForEdit?.value.forEach((item) => {
          tempEditValues.push(String(item));
        });

        Object.keys(data).forEach((items: string, index: number) => {
          if (index === 0) {
            tempOptions[items] = Object.values(data)[1].filter(
              (selectedGroup: IGroupChipSelectOption) =>
                tempEditValues.includes(String(selectedGroup.value))
            );
          } else {
            tempOptions[items] = Object.values(data)[1].filter(
              (selectedGroup: IGroupChipSelectOption) =>
                !tempEditValues.includes(String(selectedGroup.value))
            );
          }
        });
      } else {
        tempOptions = data;
      }

      setDataOptions(tempOptions);
    }
  };

  const getRuleValidation = async () => {
    const data = await getValidationAll();
    if (data) {
      setValidation(data);
    }
  };

  const getSpecificValidation = async (selectedValue: string) => {
    setIsLoaderActive(true);
    const data = await getValidation(selectedValue);
    setIsLoaderActive(false);
    if (data) {
      setSpecificValidation(data);
    }
  };

  const getLabel = (labelIdentificator: string) => {
    setLabelInputs(getLabels(labelIdentificator));
  };
  const isWaypointCondition = (condition: string) => {
    return waypointsKeys.includes(condition);
  };

  const isCheckboxGroupCondition = (condition: string) => {
    return doorsKeys.includes(condition);
  };

  // const isRangeCondition = (condition) => {
  //   return rangeName.includes(condition);
  // };

  const getAlarmRules = async () => {
    setIsLoaderActive(true);
    const data = await getRules(props.rideOwnerShipValue, props.ridePublicityValue);
    setIsLoaderActive(false);

    if (data) {
      const filteredDataForEdit: IGroupSelectOptions = Object.assign(data);

      const tempDataEdit: IGroupSelectOptions = {};

      Object.keys(filteredDataForEdit).forEach((items: string, index: number) => {
        tempDataEdit[items] = Object.values(filteredDataForEdit)[index].filter(
          (values: IOption) =>
            !props.selectedChips.find(
              (item: ISelectedEditChips) =>
                item.id === values.value && item.id !== props.dataForEdit?.type
            )
        );

        if (tempDataEdit[items].length === 0) {
          delete tempDataEdit[items];
        }
      });

      setConditionSelectOption(tempDataEdit);

      if (props.dataForEdit) {
        const editFieldName = props.dataForEdit.type;
        const editValue = props.dataForEdit.value;
        setValue('condition', editFieldName);

        if (isRangeCondition(editFieldName)) {
          if (typeof editValue === 'object' && 'min' in editValue)
            if (editValue.min === null) {
              setValue('min', '');
            } else {
              setValue('min', String(editValue.min));
            }
          if (typeof editValue === 'object' && 'max' in editValue)
            if (editValue?.max === null) {
              setValue('max', '');
            } else {
              setValue('max', String(editValue.max));
            }

          getSpecificValidation(editFieldName);
          getLabel(editFieldName);
        } else if (isCheckboxGroupCondition(editFieldName)) {
          const tempEditDoors: string[] = [];
          if (Array.isArray(editValue)) {
            editValue.forEach((items) => {
              tempEditDoors.push(String(items));
            });
          }
          getLabel(editFieldName);
          getSpecificValidation(editFieldName);
          getDataForOptions(editFieldName);
          setValue('doors', tempEditDoors);
        } else if (isWaypointCondition(editFieldName)) {
          const tempEditWaypoints: string[] = [];
          if (Array.isArray(editValue)) {
            editValue.forEach((items) => {
              tempEditWaypoints.push(String(items));
            });
          }
          getDataForOptions(editFieldName);
          getLabel(editFieldName);
          getSpecificValidation(editFieldName);

          setValue(editFieldName as TRuleForm, {
            [f({ id: 'alarms.modal.rule.choice.select' })]: tempEditWaypoints,
            [f({ id: 'alarms.modal.rule.choice.not.select' })]: [],
          });
        } else {
          getLabel(editFieldName);
          getSpecificValidation(editFieldName);
          getDataForOptions(editFieldName);
          setValue(editFieldName as TRuleForm, String(editValue));
        }
      }
    }
  };

  const results = useQueries({
    queries: [
      {
        queryKey: ['ruleFormRules'],
        queryFn: () => getAlarmRules(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: !!props.selectedChips,
      },
      {
        queryKey: ['ruleFormValidation'],
        queryFn: () => getRuleValidation(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });
  const { isLoading: isLoadingRules } = results[0];
  const { isLoading: isLoadingValidation } = results[1];
  const condition = watch('condition') as keyof IRuleFormValues;
  const selectedValue = getValues()[condition];

  const isLoadingData = isLoadingRules || isLoadingValidation || isLoaderActive;

  return (
    <CmdModal
      id="rule-form-modal"
      title={f({ id: 'alarms.modal.rule.title' })}
      footerButtons={buttons}
      loader={isLoadingData}
      // modalFooterCheckbox={f({ id: 'alarms.modal.rule.nextForm' })}
      // modalFooterCheckboxTitle={f({ id: 'alarms.modal.rule.nextForm.tooltip' })}
      // modalFooterCheckboxCallback={nextForm}
    >
      {Object.keys(conditionSelectOption).length > 0 && (
        <Controller
          name="condition"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <GroupSelectHookForm
              {...field}
              id="condition"
              label={f({ id: 'alarms.modal.rule.conditions.select.title' })}
              placeholder={f({
                id: 'alarms.modal.rule.conditions.select.placeholder.choose',
              })}
              options={conditionSelectOption}
              disabled={true}
              newStyle={true}
              searchPlaceholder={f({
                id: 'alarms.modal.rule.waypoint.search.placeholder',
              })}
              tooltip={f({ id: 'alarms.modal.rule.conditions.select.tooltip' })}
              // reload={isReset}
              control={control}
              required={true}
            />
          )}
        />
      )}

      {validation && (
        <>
          {watch('condition') && dataOptions && isWaypointCondition(watch('condition')) && (
            <Controller
              name={watch('condition') as TRuleForm}
              control={control}
              rules={{
                required: errors[condition]?.message
                  ? { value: true, message: String(errors[condition]?.message) }
                  : undefined,
              }}
              render={({ field }) => (
                <GroupChipSelectHookForm
                  {...field}
                  id="waypoint_pick"
                  // name={watch('condition')}
                  label={f({ id: 'alarms.modal.rule.waypoint.pick' })}
                  placeholder={f({ id: 'form.choose' })}
                  searchPlaceholder={f({
                    id: 'alarms.modal.rule.waypoint.search.placeholder',
                  })}
                  selectButton={f({
                    id: 'alarms.modal.rule.waypoint.select.button',
                  })}
                  resetButton={f({
                    id: 'alarms.modal.rule.waypoint.reset.button',
                  })}
                  options={dataOptions as IGroupChipSelectOptions}
                  selectedValues={
                    selectedValue &&
                    typeof selectedValue === 'object' &&
                    !Array.isArray(selectedValue)
                      ? (selectedValue as Record<string, string[]>)
                      : undefined
                  }
                  required={true}
                  onChangeCallback={(_number, values) => {
                    if (values.length > 0) {
                      setValue(watch('condition') as TRuleForm, {
                        [f({ id: 'alarms.modal.rule.choice.select' })]: [...new Set([...values])],
                        [f({ id: 'alarms.modal.rule.choice.not.select' })]: [],
                      });
                      trigger(watch('condition') as TRuleForm);
                    } else {
                      setValue(watch('condition') as TRuleForm, {
                        [f({ id: 'alarms.modal.rule.choice.select' })]: [],
                        [f({ id: 'alarms.modal.rule.choice.not.select' })]: [],
                      });
                      trigger(watch('condition') as TRuleForm);
                    }
                    trackEvent(['alarm', 'change', 'Select waypoint']);
                  }}
                  isNewGroup={true}
                  itemsNumber={3}
                  control={control}
                />
              )}
            />
          )}

          {labelInputs &&
            labelInputs.type === 'binary' &&
            dataOptions &&
            watch('condition') &&
            !isWaypointCondition(watch('condition')) &&
            !isCheckboxGroupCondition(watch('condition')) &&
            !isRangeCondition(watch('condition')) &&
            !isCheckboxCondition(watch('condition')) && (
              <Controller
                name={watch('condition') as TRuleForm}
                control={control}
                render={({ field }) => (
                  <RadioInputHookForm
                    {...field}
                    id={watch('condition')}
                    key={watch('condition')}
                    label=""
                    control={control}
                    options={dataOptions as IRadioInputValue[]}
                    required={false}
                    onChangeCallback={(value) => {
                      setValue(watch('condition') as TRuleForm, value);
                      trigger(watch('condition') as TRuleForm);
                      trackEvent(['alarm', 'change', `Select binary, ${watch('condition')}`]);
                    }}
                    tooltipByValue={radioBtnTooltipsTranslationKeys[watch('condition')]}
                  />
                )}
              />
            )}

          {labelInputs &&
            labelInputs.type === 'binary' &&
            watch('condition') &&
            dataOptions &&
            !isWaypointCondition(watch('condition')) &&
            !isCheckboxGroupCondition(watch('condition')) &&
            !isRangeCondition(watch('condition')) &&
            isCheckboxCondition(watch('condition')) && (
              <Controller
                name={watch('condition') as TRuleForm}
                control={control}
                render={({ field }) => (
                  <CheckboxInputHookForm
                    {...field}
                    id={watch('condition')}
                    key={watch('condition')}
                    label=""
                    control={control}
                    options={dataOptions as ICheckboxGroupOption[]}
                    required={
                      validation
                        ? FormikValidatorService.isFieldRequired(validation.fieldName)
                        : false
                    }
                    onChangeCallback={(value) => {
                      if (value) {
                        if (Array.isArray(dataOptions) && dataOptions.length > 0) {
                          setValue(watch('condition') as TRuleForm, dataOptions[0].value);
                        }
                        // setValue(watch('condition') as TRuleForm, dataOptions[0].value);
                        trigger(watch('condition') as TRuleForm);
                      }
                    }}
                  />
                )}
              />
            )}

          {dataOptions && watch('condition') && isCheckboxGroupCondition(watch('condition')) && (
            <Controller
              name={watch('condition') as TRuleForm}
              control={control}
              render={({ field }) => (
                <CheckboxGroupHookForm
                  {...field}
                  label={f({
                    id: 'alarm.modal.rule.doors.open',
                  })}
                  id={watch('condition')}
                  options={dataOptions as ICheckboxGroupOption[]}
                  required={
                    validation
                      ? FormikValidatorService.isFieldRequired(validation[watch('condition')])
                      : false
                  }
                  control={control}
                  onChangeCallback={(value) => {
                    setValue(watch('condition') as TRuleForm, value);
                    trigger(watch('condition') as TRuleForm);
                    trackEvent(['alarm', 'change', `Select checkbox, ${watch('condition')}`]);
                  }}
                />
              )}
            />
          )}

          {labelInputs && labelInputs.type === 'range' && isRangeCondition(watch('condition')) && (
            <>
              <Controller
                name={'min' as TRuleForm}
                control={control}
                render={({ field }) => (
                  <CmdInput
                    {...field}
                    label={f({
                      id: `${labelInputs.indexIdMin}`,
                    })}
                    id="min"
                    type="number"
                    placeholder={f({
                      id: `${labelInputs.placeholder}`,
                    })}
                    required={
                      specificValidation
                        ? FormikValidatorService.isFieldRequired(specificValidation.min)
                        : false
                    }
                    onChangeCallback={(value) => {
                      setValue('min' as TRuleForm, value);
                      trigger('min' as TRuleForm);
                      trackEvent(['alarm', 'change', `Select min, ${watch('condition')}`]);
                    }}
                    error={errors.min?.message}
                    touched={true}
                  />
                )}
              />
              <Controller
                name={'max' as TRuleForm}
                control={control}
                render={({ field }) => (
                  <CmdInput
                    {...field}
                    label={
                      labelInputs.indexIdMax
                        ? f({
                            id: `${labelInputs.indexIdMax}`,
                          })
                        : ''
                    }
                    id="max"
                    type="number"
                    placeholder={f({
                      id: `${labelInputs.placeholder}`,
                    })}
                    required={
                      specificValidation
                        ? FormikValidatorService.isFieldRequired(specificValidation.max)
                        : false
                    }
                    onChangeCallback={(value) => {
                      setValue('max' as TRuleForm, value);
                      trigger('max' as TRuleForm);
                      trackEvent(['alarm', 'change', `Select max, ${watch('condition')}`]);
                    }}
                    error={errors.max?.message}
                    touched={true}
                  />
                )}
              />
            </>
          )}
        </>
      )}
    </CmdModal>
  );
}

export default EditRuleForm;
